import { render } from "preact";
import { useRef, useState, type Dispatch, type StateUpdater } from "preact/hooks";

/***
 *  HTML changes here should be synced/copied to .cshtml file for SEO!
 * ***/

let MAX_CATEGORY_VALUE = -1;
let COUNT_CATEGORIES = -1;
let MAX_TOTAL_VALUE = -1;

function adjustCategoryValues(diff: number, categoryMap: { value: number; setValue: Dispatch<StateUpdater<number>> }[]) {
  let remainder = diff;
  if (remainder === 0) {
    return;
  }

  let categories = getNonMinCategories(categoryMap);
  if (diff > 0) {
    categories = getNonMaxCategories(categoryMap);
  }
  // radomize the categories
  categories = categories.sort(() => Math.random() - 0.5);

  for (const cat of categories) {
    if (remainder === 0) {
      break;
    }
    if (diff > 0) {
      const canDealMax = MAX_CATEGORY_VALUE - cat.value;
      const canDeal = Math.min(canDealMax, remainder);
      cat.setValue(cat.value + canDeal);
      remainder -= canDeal;
    } else {
      const canRemoveMax = cat.value;
      const canRemove = Math.max(-canRemoveMax, remainder);
      cat.setValue(cat.value + canRemove);
      remainder -= canRemove;
    }
  }
}

const getValue = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const step = Number.parseInt(target.step);
  const max = Number.parseInt(target.max);
  const min = Number.parseInt(target.min);
  // closest 100 value
  const roundedValue = Math.round(Number.parseInt(target.value) / step) * step;
  if (roundedValue > max) {
    return max;
  }
  if (roundedValue < min) {
    return min;
  }
  return roundedValue;
};

type CategoryMap = Array<{
  value: number;
  setValue: Dispatch<StateUpdater<number>>;
}>;

function getNonMaxCategories(categories: CategoryMap): CategoryMap {
  return categories.filter((item) => item.value < MAX_CATEGORY_VALUE);
}

function getNonMinCategories(categories: CategoryMap): CategoryMap {
  return categories.filter((item) => item.value > 0);
}

function BonusCalculatorV2(props: Readonly<{ htmlContainer: HTMLDivElement }>) {
  const settings = (props.htmlContainer.closest(`[data-bonus-calculator]`) as HTMLDivElement).dataset;
  const {
    textCardOne,
    textCardTwo,
    textMonthlySpend,
    textLinkCardOne,
    textHrefLinkCardOne,
    textLinkCardTwo,
    textHrefLinkCardTwo,
    textPerMonth,
    textAbroad,
    textOnline,
    categories,
    monthlyDefaultValue,
    hideCategories: strHideCategories,
    hidePerMonth: strHidePerMonth,
    maxValueCategory,
    countCategories,
  } = settings;
  const hideCategories = strHideCategories === "true";
  const hidePerMonth = strHidePerMonth === "true" && !hideCategories;
  COUNT_CATEGORIES = Number.parseInt(countCategories ?? "9");
  MAX_CATEGORY_VALUE = Number.parseInt(maxValueCategory ?? "10000");
  MAX_TOTAL_VALUE = MAX_CATEGORY_VALUE * COUNT_CATEGORIES;

  if (!categories) {
    return "Could not find categories";
  }
  let parsedCategories: Array<{
    htmlId: string;
    text: string;
    defaultValue: number;
  }> = [];
  try {
    parsedCategories = JSON.parse(categories);
  } catch (e) {
    return "Could not parse categories";
  }

  let previousMonthlyCost = useRef(Number.parseInt(`${monthlyDefaultValue ?? 0}`));

  const [monthlyValue, setMontlyValue] = useState(Number.parseInt(`${monthlyDefaultValue ?? 0}`));

  const decreaseOnlineAbroadOnMonthlyDecrease = (diff: number) => {
    if (diff >= 0) return;
    if (abroadValue <= 0 && onlineValue <= 0) return;
    if (abroadValue + onlineValue <= monthlyValue + diff) return;

    const getNextValue = (prev: number) => {
      return Math.max(0, prev + diff);
    };
    const getNextHalfValue = (prev: number) => {
      return Math.max(0, prev + diff / 2);
    };

    if (abroadValue > 0 && onlineValue > 0) {
      if (diff <= -100) {
        if (abroadValue > onlineValue) {
          setAbroadValue(getNextValue);
        } else {
          setOnlineValue(getNextValue);
        }
      } else {
        setAbroadValue(getNextHalfValue);
        setOnlineValue(getNextHalfValue);
      }
      return;
    }
    if (abroadValue > 0) {
      setAbroadValue(getNextValue);
      return;
    }
    setOnlineValue(getNextValue);
  };

  const updateMonthlyValueOnCategoryChange = (e: Event, oldCatValue: number) => {
    const newCatValue = getValue(e);
    const diff = newCatValue - oldCatValue;

    if (diff === 0) return;

    setMontlyValue((prev) => {
      const next = prev + diff;
      const nextValue = Math.min(MAX_TOTAL_VALUE, Math.max(0, next));
      previousMonthlyCost.current = nextValue;
      return nextValue;
    });

    decreaseOnlineAbroadOnMonthlyDecrease(diff);
  };

  const [clothesValue, setClothesValue] = useState(parsedCategories[0].defaultValue);
  const onChangeClothesValue = (e: Event) => {
    updateMonthlyValueOnCategoryChange(e, clothesValue);
    setClothesValue(getValue(e));
  };

  const [pleasureValue, setPleasureValue] = useState(parsedCategories[1].defaultValue);
  const onChangePleasureValue = (e: Event) => {
    updateMonthlyValueOnCategoryChange(e, pleasureValue);
    setPleasureValue(getValue(e));
  };

  const [vacationValue, setVacationValue] = useState(parsedCategories[2].defaultValue);
  const onChangeVacationValue = (e: Event) => {
    updateMonthlyValueOnCategoryChange(e, vacationValue);
    setVacationValue(getValue(e));
  };

  const [leisureValue, setLeisureValue] = useState(parsedCategories[3].defaultValue);
  const onChangeLeisureValue = (e: Event) => {
    updateMonthlyValueOnCategoryChange(e, leisureValue);
    setLeisureValue(getValue(e));
  };

  const [foodValue, setFoodValue] = useState(parsedCategories[4].defaultValue);
  const onChangeFoodValue = (e: Event) => {
    updateMonthlyValueOnCategoryChange(e, foodValue);
    setFoodValue(getValue(e));
  };

  const [transportValue, setTransportValue] = useState(parsedCategories[5].defaultValue);
  const onChangeTransportValue = (e: Event) => {
    updateMonthlyValueOnCategoryChange(e, transportValue);
    setTransportValue(getValue(e));
  };

  const [decorationValue, setDecorationValue] = useState(parsedCategories[6].defaultValue);
  const onChangeDecorationValue = (e: Event) => {
    updateMonthlyValueOnCategoryChange(e, decorationValue);
    setDecorationValue(getValue(e));
  };

  const [electronicsValue, setElectronicsValue] = useState(parsedCategories[7].defaultValue);
  const onChangeElectronicsValue = (e: Event) => {
    updateMonthlyValueOnCategoryChange(e, electronicsValue);
    setElectronicsValue(getValue(e));
  };

  const [otherCategoryValue, setOtherCategoryValue] = useState(parsedCategories[8].defaultValue);
  const onChangeOtherCategoryValue = (e: Event) => {
    updateMonthlyValueOnCategoryChange(e, otherCategoryValue);
    setOtherCategoryValue(getValue(e));
  };

  const categoryMap: Array<{
    value: number;
    setValue: Dispatch<StateUpdater<number>>;
  }> = [
    {
      value: clothesValue,
      setValue: setClothesValue,
    },
    {
      value: pleasureValue,
      setValue: setPleasureValue,
    },
    {
      value: vacationValue,
      setValue: setVacationValue,
    },
    {
      value: leisureValue,
      setValue: setLeisureValue,
    },
    {
      value: foodValue,
      setValue: setFoodValue,
    },
    {
      value: transportValue,
      setValue: setTransportValue,
    },
    {
      value: decorationValue,
      setValue: setDecorationValue,
    },
    {
      value: electronicsValue,
      setValue: setElectronicsValue,
    },
    {
      value: otherCategoryValue,
      setValue: setOtherCategoryValue,
    },
  ];

  const onChangeMonthlyValue = (e: Event) => {
    const value = getValue(e);
    const diff = value - previousMonthlyCost.current;

    adjustCategoryValues(diff, categoryMap);
    setMontlyValue(value);
    decreaseOnlineAbroadOnMonthlyDecrease(diff);

    previousMonthlyCost.current = value;
  };

  const [onlineValue, setOnlineValue] = useState(0);
  const onChangeOnlineValue = (e: Event) => {
    const newOnline = getValue(e);
    const overflow = newOnline + abroadValue - monthlyValue;
    setOnlineValue(newOnline);

    if (overflow > 0) {
      setAbroadValue((prev) => Math.max(0, prev - overflow));
    }
  };

  const [abroadValue, setAbroadValue] = useState(0);
  const onChangeAbroadValue = (e: Event) => {
    const newAbroad = getValue(e);
    const overflow = onlineValue + newAbroad - monthlyValue;
    setAbroadValue(newAbroad);

    if (overflow > 0) {
      setOnlineValue((prev) => Math.max(0, prev - overflow));
    }
  };

  const { mastercardBonusPercent, premiumBonusPercent, sasBonusPercent, abroadBonusPercent } = settings;

  const BONUS_REGULAR_PERCENT = Number.parseFloat(mastercardBonusPercent!);
  const BONUS_PREMIUM_PERCENT = Number.parseFloat(premiumBonusPercent!);
  const BONUS_PURCHASE_SAS_ONLINE_PERCENT = Number.parseFloat(sasBonusPercent!);
  const BONUS_ABROAD_PERCENT = Number.parseFloat(abroadBonusPercent!);

  let totalRegularExpenses = monthlyValue;
  totalRegularExpenses -= onlineValue;
  totalRegularExpenses -= abroadValue;

  const REGULAR_TOTAL_PERCENT = BONUS_REGULAR_PERCENT / 100;
  const PREMIUM_TOTAL_PERCENT = BONUS_PREMIUM_PERCENT / 100;

  const SAS_TOTAL_PERCENT = (BONUS_REGULAR_PERCENT + BONUS_PURCHASE_SAS_ONLINE_PERCENT) / 100;
  const SAS_TOTAL_PREMIUM_PERCENT = (BONUS_PREMIUM_PERCENT + BONUS_PURCHASE_SAS_ONLINE_PERCENT) / 100;

  const ABROAD_TOTAL_PERCENT = (BONUS_REGULAR_PERCENT + BONUS_ABROAD_PERCENT) / 100;
  const ABROAD_TOTAL_PERCENT_PREMIUM = (BONUS_PREMIUM_PERCENT + BONUS_ABROAD_PERCENT) / 100;

  // Mastercard
  const regularBonus = (totalRegularExpenses * REGULAR_TOTAL_PERCENT + onlineValue * SAS_TOTAL_PERCENT + abroadValue * ABROAD_TOTAL_PERCENT) * 12;

  // Premium
  const premiumBonus = (totalRegularExpenses * PREMIUM_TOTAL_PERCENT + onlineValue * SAS_TOTAL_PREMIUM_PERCENT + abroadValue * ABROAD_TOTAL_PERCENT_PREMIUM) * 12;

  const monthlyPercent = Math.round((monthlyValue / MAX_TOTAL_VALUE) * 100 * 100) / 100;
  const monthlyColor = "var(--pal-bonus-line-primary)";

  const onlinePercent = Math.round((onlineValue / monthlyValue) * 100 * 100) / 100;
  const abroadPercent = Math.round((abroadValue / monthlyValue) * 100 * 100) / 100;

  return (
    <>
      <div style={hidePerMonth ? { display: "none" } : {}} class="background-white sas-bonus-calculator__total" id="per-month">
        <div class="sas-bonus-calculator__total-row sas-bonus-calculator__total-row--month-total">
          <label class="sas-bonus-calculator__label sas-bonus-calculator__label--month-total">
            <span class="sas-bonus-calculator__label-text sas-bonus-calculator__label-text--month-total">{textMonthlySpend}</span>
            <input
              id="bonus-monthly-spend"
              class="sr-only sas-bonus-calculator__input"
              data-monthly-expense=""
              min="0"
              max={MAX_TOTAL_VALUE}
              name="total"
              step="100"
              type="number"
              onChange={onChangeMonthlyValue}
              value={monthlyValue}
            />
            <span data-id="bonus-monthly-spend" data-pretty-input="" aria-hidden="true" class="sas-bonus-calculator__pretty-input">
              {monthlyValue.toLocaleString("sv-SE", {
                useGrouping: true,
              })}
            </span>
            <span class="sas-bonus-calculator__currency">kr</span>
          </label>
        </div>
        <div class="sas-bonus-calculator__total-row sas-bonus-calculator__total-wrap-range">
          <input
            value={monthlyValue}
            onInput={onChangeMonthlyValue}
            type="range"
            data-id="bonus-monthly-spend"
            aria-hidden="true"
            class="sas-bonus-calculator__range-input"
            step="100"
            max={MAX_TOTAL_VALUE}
            min="0"
            tabIndex={-1}
            style={{
              background: `linear-gradient(${monthlyColor}, ${monthlyColor}) 0 center / ${monthlyPercent}% 4px no-repeat, transparent`,
            }}
          />
          <div class="sas-bonus-calculator__span sas-bonus-calculator__total-span">
            <div aria-hidden="true">0</div>
            <div aria-hidden="true" data-pretty-max="">
              {MAX_TOTAL_VALUE.toLocaleString("sv-SE", {
                useGrouping: true,
              })}
            </div>
          </div>
        </div>
      </div>

      <div class="row sas-bonus-calculator__grid-row" id="categories" style={hideCategories ? { display: "none" } : {}}>
        {parsedCategories.map((cat) => {
          let value = -1;
          let setValue = (_e: Event) => {};
          if (cat.htmlId === "calculator_category_0") {
            value = clothesValue;
            setValue = onChangeClothesValue;
          }
          if (cat.htmlId === "calculator_category_1") {
            value = pleasureValue;
            setValue = onChangePleasureValue;
          }
          if (cat.htmlId === "calculator_category_2") {
            value = vacationValue;
            setValue = onChangeVacationValue;
          }
          if (cat.htmlId === "calculator_category_3") {
            value = leisureValue;
            setValue = onChangeLeisureValue;
          }
          if (cat.htmlId === "calculator_category_4") {
            value = foodValue;
            setValue = onChangeFoodValue;
          }
          if (cat.htmlId === "calculator_category_5") {
            value = transportValue;
            setValue = onChangeTransportValue;
          }
          if (cat.htmlId === "calculator_category_6") {
            value = decorationValue;
            setValue = onChangeDecorationValue;
          }
          if (cat.htmlId === "calculator_category_7") {
            value = electronicsValue;
            setValue = onChangeElectronicsValue;
          }
          if (cat.htmlId === "calculator_category_8") {
            value = otherCategoryValue;
            setValue = onChangeOtherCategoryValue;
          }
          return <Category key={cat.htmlId} name={cat.text} value={value} setValue={setValue} htmlId={cat.htmlId} />;
        })}
      </div>
      <div class={`row sas-bonus-calculator__grid-row ${hideCategories ? "" : "sas-bonus-calculator__grid-row--whereof"}`} id="where-of">
        <div class="column large-6 sas-bonus-calculator__column sas-bonus-calculator__column--wherof">
          <div class="sas-bonus-calculator__item">
            <div class="sas-bonus-calculator__row">
              <label class="sas-bonus-calculator__label">
                <span class="sas-bonus-calculator__label-text sas-bonus-calculator__label-text--whereof">{textOnline}</span>
                <input
                  value={onlineValue}
                  onChange={onChangeOnlineValue}
                  type="number"
                  id="bonus-online-spend"
                  class="sr-only sas-bonus-calculator__input"
                  data-online-expense=""
                  name="total"
                  step="100"
                  min="0"
                  max={monthlyValue}
                />
                <span data-id="bonus-online-spend" data-pretty-input="" class="sas-bonus-calculator__pretty-input" aria-hidden="true">
                  {onlineValue.toLocaleString("sv-SE", {
                    useGrouping: true,
                  })}
                </span>
                <span class="sas-bonus-calculator__currency">kr</span>
              </label>
            </div>
            <div class="sas-bonus-calculator__row sas-bonus-calculator__wrap-range">
              <input
                value={onlineValue}
                onInput={onChangeOnlineValue}
                type="range"
                data-id="bonus-online-spend"
                aria-hidden="true"
                class="sas-bonus-calculator__range-input sas-bonus-calculator__range-input--whereof"
                step="100"
                max={monthlyValue}
                min="0"
                tabindex={-1}
                data-range-online-expense=""
                data-color-rangeslider="var(--pal-bonus-line-secondary)"
                style={{
                  background: `linear-gradient(var(--pal-bonus-line-secondary), var(--pal-bonus-line-secondary)) 0 center / ${onlinePercent}% 4px no-repeat, transparent`,
                }}
              />
              <div class="sas-bonus-calculator__span">
                <div aria-hidden="true">0</div>
                <div aria-hidden="true" data-pretty-max="" data-pretty-range-online-max="">
                  {monthlyValue.toLocaleString("sv-SE", {
                    useGrouping: true,
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="column large-6 sas-bonus-calculator__column sas-bonus-calculator__column--wherof">
          <div class="sas-bonus-calculator__item">
            <div class="sas-bonus-calculator__row">
              <label class="sas-bonus-calculator__label">
                <span class="sas-bonus-calculator__label-text sas-bonus-calculator__label-text--whereof">{textAbroad}</span>
                <input
                  value={abroadValue}
                  onChange={onChangeAbroadValue}
                  type="number"
                  id="bonus-abroad-spend"
                  class="sr-only sas-bonus-calculator__input"
                  data-abroad-expense=""
                  name="total"
                  step="100"
                  min="0"
                  max={monthlyValue}
                />
                <span data-id="bonus-abroad-spend" data-pretty-input="" class="sas-bonus-calculator__pretty-input" aria-hidden="true">
                  {abroadValue.toLocaleString("sv-SE", {
                    useGrouping: true,
                  })}
                </span>
                <span class="sas-bonus-calculator__currency">kr</span>
              </label>
            </div>
            <div class="sas-bonus-calculator__row sas-bonus-calculator__wrap-range">
              <input
                value={abroadValue}
                onInput={onChangeAbroadValue}
                type="range"
                data-id="bonus-abroad-spend"
                aria-hidden="true"
                class="sas-bonus-calculator__range-input sas-bonus-calculator__range-input--whereof"
                step="100"
                max={monthlyValue}
                min="0"
                tabindex={-1}
                data-range-abroad-expense=""
                data-color-rangeslider="var(--pal-bonus-line-secondary)"
                style={{
                  background: `linear-gradient(var(--pal-bonus-line-secondary), var(--pal-bonus-line-secondary)) 0 center / ${abroadPercent}% 4px no-repeat, transparent`,
                }}
              />
              <div class="sas-bonus-calculator__span">
                <div aria-hidden="true">0</div>
                <div aria-hidden="true" data-pretty-max="" data-pretty-range-abroad-max="">
                  {monthlyValue.toLocaleString("sv-SE", {
                    useGrouping: true,
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row sas-bonus-calculator__row-result">
        <div class="column background-blue-dark large-6 sas-bonus-calculator__result">
          <div class="wysiwyg-output">
            <h3>{textCardOne}</h3>
          </div>
          <p class="sas-body sas-bonus-calculator__points-result">
            <span class="sas-bonus-calculator__result-points" data-regular-bonus="">
              {regularBonus.toLocaleString("sv-SE", {
                useGrouping: true,
              })}
            </span>
            <span class="sas-body sas-bonus-calculator__result-text">{textPerMonth}</span>
          </p>

          <p class="sas-body sas-bonus-calculator__result-wrap-button">
            <a href={textHrefLinkCardOne} class="button button--contrast sas-bonus-calculator__result-button">
              {textLinkCardOne}
            </a>
          </p>
        </div>

        <div class="column background-blue-dark large-6 sas-bonus-calculator__result">
          <div class="wysiwyg-output">
            <h3>{textCardTwo}</h3>
          </div>
          <p class="sas-body sas-bonus-calculator__points-result">
            <span class="sas-bonus-calculator__result-points" data-premium-bonus="">
              {premiumBonus.toLocaleString("sv-SE", {
                useGrouping: true,
              })}
            </span>
            <span class="sas-body sas-bonus-calculator__result-text">{textPerMonth}</span>
          </p>

          <p class="sas-body sas-bonus-calculator__result-wrap-button">
            <a href={textHrefLinkCardTwo} class="button button--contrast sas-bonus-calculator__result-button">
              {textLinkCardTwo}
            </a>
          </p>
        </div>
      </div>
    </>
  );
}

function Category(props: Readonly<{ name: string; htmlId: string; value: number; setValue: (e: Event) => void }>) {
  const htmlId = props.htmlId;
  const percent = Math.round((props.value / MAX_CATEGORY_VALUE) * 100 * 100) / 100;
  const color = "var(--pal-bonus-line-primary)";
  return (
    <div class="column large-4 sas-bonus-calculator__column">
      <div class="sas-bonus-calculator__item">
        <div class="sas-bonus-calculator__row">
          <label class="sas-bonus-calculator__label">
            <span class="sas-bonus-calculator__label-text">{props.name}</span>
            <input
              type="number"
              value={props.value}
              id={htmlId}
              onChange={props.setValue}
              class="sr-only sas-bonus-calculator__input"
              data-expense=""
              min="0"
              max="10000"
              name="total"
              step="100"
            />
            <span data-id={htmlId} data-pretty-input="" aria-hidden="true" class="sas-bonus-calculator__pretty-input">
              {props.value.toLocaleString("sv-SE", {
                useGrouping: true,
              })}
            </span>
            <span class="sas-bonus-calculator__currency">kr</span>
          </label>
        </div>
        <div class="sas-bonus-calculator__row sas-bonus-calculator__wrap-range">
          <input
            type="range"
            value={props.value}
            onInput={props.setValue}
            data-id={htmlId}
            tabIndex={-1}
            aria-hidden="true"
            class="sas-bonus-calculator__range-input"
            step="100"
            max="10000"
            min="0"
            style={{
              background: `linear-gradient(${color}, ${color}) 0 center / ${percent}% 4px no-repeat, transparent`,
            }}
          />
          <div class="sas-bonus-calculator__span">
            <div aria-hidden="true">0</div>
            <div aria-hidden="true" data-pretty-max="">
              10&nbsp;000
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

document.querySelectorAll<HTMLDivElement>(`[data-bonus-app]`).forEach((calculator) => {
  return render(<BonusCalculatorV2 htmlContainer={calculator} />, calculator);
});
